






































































































import Vue from 'vue'
import { mapState } from 'vuex'
import { ApiResponse, InventoryItem, ItemFile, Project } from '@/interfaces'
import HighlightIcon from '@/components/Icons/HighlightIcon.vue'

interface Rubrique {
  label: string
  value: string
  children: Rubrique[]
}

interface DropdownAction {
  action: string
  item?: InventoryItem
  index?: number
}

export default Vue.extend({
  components: { HighlightIcon },
  props: {
    item: {
      type: Object as () => InventoryItem,
      required: true,
    },
    project: {
      type: Object as () => Project,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      rubriques: [] as Rubrique[],
      isDragActive: false,
      isUploadingItemFiles: false,
    }
  },
  computed: {
    ...mapState(['user']),
    cleanedFiles(): ItemFile[] {
      return this.cleanInventoryItemFiles(this.item.inventoryItemFiles)
    },
    galleryImages(): string[] {
      return this.getMedias(this.cleanedFiles, 'image')
    },
    thumbnailUrl(): string {
      return this.getThumbnails(this.cleanedFiles, 'image')[0] ?? ''
    },
    mediaCount(): number {
      return this.galleryImages.length
    },
  },
  watch: {
    user(newVal) {
      this.rubriques = newVal.client?.config.rubriques
    },
  },
  mounted() {
    if (this.user) {
      this.rubriques = this.user.client?.config.rubriques
    }
  },
  methods: {
    cleanInventoryItemFiles(files: ItemFile[]) {
      return files.filter(this.cleanDeletedFiles).sort(this.sortByPosition)
    },
    cleanDeletedFiles(file: ItemFile) {
      return file.deletedAt !== 'NULL'
    },
    sortByPosition(file1: ItemFile, file2: ItemFile) {
      if (file1.position > file2.position) {
        return 1
      }

      if (file1.position < file2.position) {
        return -1
      }

      return 0
    },
    getMedias(files: ItemFile[], type: string) {
      let medias = [] as string[]
      for (let index = 0; index < files.length; index++) {
        const element = files[index]

        if (element.type == type && element.deletedAt == null) {
          medias.push(element.url)
        }
      }

      return medias
    },
    getThumbnails(files: ItemFile[], type: string) {
      let medias = [] as string[]
      for (let index = 0; index < files.length; index++) {
        const element = files[index]
        if (element.type == type && element.deletedAt == null) {
          medias.push(element.urlThumb)
          return medias
        }
      }

      return medias
    },
    getEstimatePrice(item: InventoryItem) {
      let estimation = ''
      if (item.value1 !== null) {
        const value1 = item.value1 == '0' ? 'Mémoire' : this.formatCurrency(item.value1)
        estimation += ' ' + value1
      }
      if (item.value2 !== null) {
        const value2 = item.value2 == '0' ? 'Mémoire' : this.formatCurrency(item.value2)
        if (item.value1 !== null) {
          estimation += ' - '
        }
        estimation += value2
      }
      if (item.value3 !== null) {
        const value3 = item.value3 == '0' ? 'Mémoire' : this.formatCurrency(item.value3)
        if (item.value1 !== null || item.value2 !== null) {
          estimation += ' - '
        }
        estimation += value3
      }
      return estimation
    },
    formatCurrency(number: string | number | null | undefined): string {
      if (typeof number === 'string') {
        number = Number(number)
      }
      if (number === null || number === undefined || Number.isNaN(number)) {
        number = 0
      }

      return Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(number)
    },
    getRubriqueRec(rubriques: Rubrique[], str: string): string {
      let rub = '-'
      rubriques.forEach((rubrique: Rubrique) => {
        if (rubrique.value == str) {
          rub = rubrique.label
        }
        if (rubrique.children && rub == '-') {
          rub = this.getRubriqueRec(rubrique.children, str)
        }
      })

      return rub
    },
    isHighlight(item: InventoryItem) {
      return item.highlight == true
    },
    toggleChecked(item: InventoryItem, index: number, event: PointerEvent | null) {
      this.$emit('toggleCheck', item, index, event)
    },
    editItem() {
      this.$emit('edit', this.item)
    },
    handleDragOver(event: DragEvent) {
      if (this.isUploadingItemFiles) {
        return
      }

      const hasFiles = !!event.dataTransfer?.types?.includes('Files')
      this.isDragActive = hasFiles
    },
    handleDragLeave(event: DragEvent) {
      const currentTarget = event.currentTarget as Node | null
      const relatedTarget = event.relatedTarget as Node | null

      if (!currentTarget || !relatedTarget || !currentTarget.contains(relatedTarget)) {
        this.isDragActive = false
      }
    },
    fileToResizedBase64(file: File): Promise<string> {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => {
          if (!reader.result) {
            reject(new Error('empty_file'))
            return
          }

          const canvas = document.createElement('canvas')
          const image = document.createElement('img')
          let context = canvas.getContext('2d')

          image.onload = () => {
            canvas.width = image.width
            canvas.height = image.height
            context?.drawImage(image, 0, 0, image.width, image.height)

            const maxWidth = 2048
            const maxHeight = 2048
            let width = image.width
            let height = image.height

            if (width > height) {
              if (width > maxWidth) {
                height *= maxWidth / width
                width = maxWidth
              }
            } else if (height > maxHeight) {
              width *= maxHeight / height
              height = maxHeight
            }

            context?.clearRect(0, 0, canvas.width, canvas.height)

            context = canvas.getContext('2d')
            canvas.width = width
            canvas.height = height
            context?.drawImage(image, 0, 0, width, height)

            resolve(canvas.toDataURL('image/jpeg', 0.75).toString().split(',')[1])
          }
          image.onerror = () => reject(new Error('resize_image_failed'))
          image.src = reader.result.toString()
        }
        reader.onerror = () => reject(reader.error ?? new Error('read_file_failed'))
      })
    },
    getNextImagePosition(): number {
      let maxPosition = 0

      const files = this.item?.inventoryItemFiles ?? []
      files.forEach((file: ItemFile) => {
        if (file.type === 'image' && file.deletedAt == null && Number.isFinite(file.position)) {
          maxPosition = Math.max(maxPosition, file.position)
        }
      })

      return Math.max(maxPosition, this.mediaCount, 0) + 1
    },
    async dropItemFiles(event: DragEvent) {
      this.isDragActive = false

      if (this.isUploadingItemFiles) {
        return
      }

      const droppedFiles = event.dataTransfer?.files
      if (!droppedFiles || droppedFiles.length === 0) {
        return
      }

      const files = Array.from(droppedFiles).filter((file) => file.type.startsWith('image/'))
      if (files.length === 0) {
        this.$notify({
          type: 'warning',
          title: 'Attention',
          message: 'Déposez au moins une image valide.',
        })
        return
      }

      if (!this.item?.id) {
        this.$notify({
          type: 'error',
          title: 'Erreur',
          message: 'Lot introuvable pour le téléversement.',
        })
        return
      }

      this.isUploadingItemFiles = true
      try {
        let nextPosition = this.getNextImagePosition()

        for (const file of files) {
          const data = await this.fileToResizedBase64(file)
          await this.$api.post(`/inventoryitem/${this.item.id}/file`, {
            filename: file.name,
            position: nextPosition,
            data,
          })

          nextPosition++
        }

        const response = await this.$api.get(`/inventory/${this.project.id}/item/${this.item.id}`)
        const apiResponse = response.data as ApiResponse

        this.$notify({
          type: 'success',
          title: 'Succès',
          message: `${files.length} image(s) ajoutée(s) au lot.`,
        })
        this.$emit('itemFilesUploaded', apiResponse.data as InventoryItem)
      } catch (error: unknown) {
        const apiError = error as { response?: { data?: { message?: string } } }
        const message = apiError?.response?.data?.message ?? 'Impossible de téléverser les images sur ce lot.'
        this.$notify({
          type: 'error',
          title: 'Erreur',
          message,
        })
      } finally {
        this.isUploadingItemFiles = false
      }
    },
    handleItemCommand(command: DropdownAction) {
      if (command.action === 'edit' && command.item) {
        this.$emit('edit', command.item)
      }

      if (command.action === 'duplicate' && command.item && command.index !== undefined) {
        this.$emit('duplicate', command.item, command.index)
      }

      if (command.action === 'ia' && command.item) {
        this.$emit('ia', command.item)
      }

      if (command.action === 'delete' && command.item) {
        this.$emit('delete', command.item)
      }
    },
  },
})
